import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Alert,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import FilePreview from './FilePreview';

const FileUpload = ({ onFileSelect, acceptedFiles = [] }) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    setError('');

    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      setError(`Archivo no válido: ${rejection.errors[0]?.message || 'Formato no soportado'}`);
      return;
    }

    if (acceptedFiles.length === 0) {
      setError('No se seleccionó ningún archivo válido');
      return;
    }

    const file = acceptedFiles[0];
    
    // Validar tamaño de archivo (máximo 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('El archivo es demasiado grande. Máximo 10MB permitido.');
      return;
    }

    setUploading(true);
    
    try {
      // Simular procesamiento
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setUploadedFile(file);
      onFileSelect && onFileSelect(file);
    } catch (err) {
      setError('Error al procesar el archivo');
    } finally {
      setUploading(false);
    }
  }, [onFileSelect]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    multiple: false
  });

  const removeFile = () => {
    setUploadedFile(null);
    setError('');
    onFileSelect && onFileSelect(null);
  };

  const openPreview = () => {
    setPreviewOpen(true);
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) {
      return <ImageIcon color="primary" />;
    } else if (file.type === 'application/pdf') {
      return <PdfIcon color="error" />;
    }
    return <FileIcon color="action" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ width: '100%' }}>
      {!uploadedFile ? (
        <Paper
          {...getRootProps()}
          sx={{
            border: 2,
            borderStyle: 'dashed',
            borderColor: isDragActive ? 'primary.main' : 'grey.300',
            bgcolor: isDragActive ? 'primary.50' : 'grey.50',
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'primary.50'
            }
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon 
            sx={{ 
              fontSize: 48, 
              color: isDragActive ? 'primary.main' : 'grey.400',
              mb: 2 
            }} 
          />
          <Typography variant="h6" gutterBottom>
            {isDragActive ? '¡Suelta el archivo aquí!' : 'Arrastra y suelta tu archivo aquí'}
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            o
          </Typography>
          <Button variant="contained" component="span" sx={{ mt: 1 }}>
            Seleccionar archivo
          </Button>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            Formatos soportados: imágenes (JPG, PNG, GIF, WebP) y PDF
            <br />
            Tamaño máximo: 10MB
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ p: 3, bgcolor: 'success.50', border: 1, borderColor: 'success.200' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {getFileIcon(uploadedFile)}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" component="div">
                {uploadedFile.name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {formatFileSize(uploadedFile.size)} • {uploadedFile.type}
              </Typography>
            </Box>
            <Chip label="Cargado" color="success" size="small" />
            <IconButton
              onClick={openPreview}
              color="primary"
              size="small"
              title="Ver vista previa"
            >
              <VisibilityIcon />
            </IconButton>
            <IconButton
              onClick={removeFile}
              color="error"
              size="small"
              title="Eliminar archivo"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Paper>
      )}

      {uploading && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            Procesando archivo...
          </Typography>
          <LinearProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <FilePreview
        file={uploadedFile}
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />
    </Box>
  );
};

export default FileUpload;